import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminDeleteButton } from "@/components/AdminActions";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate, formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, Loader2, Check, X, FileText, Wallet, UserCircle,
  CheckCircle2, Receipt, Building2, MessageSquare,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { EditHistory, EditExpenseDialog } from "@/components/AdminEdit";
import { RupiahInput } from "@/components/RupiahInput";
import { Pencil } from "lucide-react";
import { useAutoRefresh } from "@/lib/autoRefresh";
import { DisburseDialog, sourceLabel } from "@/components/FundSource";

function EditRequestDialog({ req, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ divisi: req.divisi || "", amount_requested: req.amount_requested ?? "", reason: req.reason || "",
      approved_amount: req.approved_amount ?? "", decision_note: req.decision_note || "", date: (req.created_at || "").slice(0, 10) });
  }, [open, req]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, amount_requested: Number(form.amount_requested) };
    if (req.approved_amount != null && form.approved_amount !== "") payload.approved_amount = Number(form.approved_amount);
    else delete payload.approved_amount;
    if (!req.decision_note && !form.decision_note) delete payload.decision_note;
    try {
      await api.put(`/admin/cash-requests/${req.id}`, payload);
      toast.success("Pengajuan diperbarui."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-brand text-brand" data-testid="admin-edit-request-btn"><Pencil className="h-4 w-4 mr-2" /> Edit</Button>
      </DialogTrigger>
      <DialogContent data-testid="edit-request-dialog">
        <DialogHeader><DialogTitle>Edit Pengajuan Kas</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Divisi</Label><Input value={form.divisi || ""} onChange={set("divisi")} className="mt-1.5" data-testid="edit-req-divisi" /></div>
            <div><Label>Tanggal Pengajuan</Label><Input type="date" value={form.date || ""} onChange={set("date")} className="mt-1.5" data-testid="edit-req-date" /></div>
            <div><Label>Jumlah Diminta</Label><RupiahInput value={form.amount_requested ?? ""} onChange={set("amount_requested")} className="mt-1.5 font-mono" data-testid="edit-req-amount" /></div>
            {req.approved_amount != null && (
              <div><Label>Jumlah Disetujui</Label><RupiahInput value={form.approved_amount ?? ""} onChange={set("approved_amount")} className="mt-1.5 font-mono" data-testid="edit-req-approved" /></div>
            )}
          </div>
          <div><Label>Alasan Penggunaan</Label><Textarea value={form.reason || ""} onChange={set("reason")} rows={3} className="mt-1.5" data-testid="edit-req-reason" /></div>
          {req.status !== "pending" && (
            <div><Label>Catatan Approval</Label><Input value={form.decision_note || ""} onChange={set("decision_note")} className="mt-1.5" data-testid="edit-req-note" /></div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-edit-request">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Info({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-brand" />
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value}</p>
      </div>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);

  // decision state
  const [approvedAmount, setApprovedAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [rk, setRk] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const isAdmin = user?.role === "it_admin";
  useEffect(() => { if (isAdmin) api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, [isAdmin]);

  const load = () => api.get(`/cash-requests/${id}`).then((r) => {
    setReq(r.data);
    setRk((x) => x + 1);
    setApprovedAmount(String(r.data.amount_requested));
  }).catch(() => toast.error("Gagal memuat pengajuan"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useAutoRefresh(load);

  if (!req) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const isApprover = ["direktur", "approver"].includes(user?.role);
  const isOwner = req.requester_id === user?.id;
  const canDecide = isApprover && req.status === "pending";
  const canDisburse = ["tim_keuangan", "direktur", "it_admin"].includes(user?.role) && req.status === "approved";
  const canUploadProof = isOwner && req.status === "disbursed";
  const approved = req.approved_amount || 0;
  const pct = approved > 0 ? Math.min(100, (req.total_expensed / approved) * 100) : 0;

  const decide = async (action) => {
    setBusy(true);
    try {
      const payload = { action, note };
      if (action === "approve") payload.approved_amount = Number(approvedAmount);
      await api.post(`/cash-requests/${id}/decision`, payload);
      toast.success(action === "approve" ? "Pengajuan disetujui." : "Pengajuan ditolak.");
      setDecisionOpen(false); setNote(""); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };


  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> Kembali
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Detail Pengajuan Kas</h2>
            <StatusBadge status={req.status} />
          </div>
          <p className="text-sm text-muted-foreground font-mono mt-1">#{req.id.slice(0, 8)} · {formatDate(req.created_at)}</p>
        </div>
        {isAdmin && <EditRequestDialog req={req} onDone={load} />}
        <AdminDeleteButton path={`/admin/records/cash-requests/${req.id}`} onDone={() => navigate("/requests")} label="Hapus Pengajuan" testId="admin-del-request"
          confirmText="Pengajuan kas beserta approval, pencairan, dan semua bukti pengeluarannya akan dihapus permanen dan hilang dari semua laporan." />
        {canDecide && (
          <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-emerald-800" data-testid="open-decision-btn">Proses Approval</Button>
            </DialogTrigger>
            <DialogContent data-testid="decision-dialog">
              <DialogHeader><DialogTitle>Proses Pengajuan Kas</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-secondary p-3 text-sm">
                  <p><span className="text-muted-foreground">Pemohon:</span> {req.requester_name}</p>
                  <p><span className="text-muted-foreground">Diminta:</span> <span className="font-mono font-semibold">{formatRupiah(req.amount_requested)}</span></p>
                </div>
                <div>
                  <Label htmlFor="appamt">Jumlah Disetujui (Rp)</Label>
                  <RupiahInput id="appamt" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)}
                    className="mt-1.5 font-mono" data-testid="approved-amount-input" />
                  <p className="text-xs text-muted-foreground mt-1">{formatRupiah(approvedAmount)} · maks {formatRupiah(req.amount_requested)}</p>
                </div>
                <div>
                  <Label htmlFor="note">Keterangan / Alasan</Label>
                  <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    placeholder="Wajib diisi jika menolak..." className="mt-1.5" data-testid="decision-note-input" />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => decide("reject")} disabled={busy}
                  className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-btn">
                  <X className="h-4 w-4 mr-1.5" /> Tolak
                </Button>
                <Button onClick={() => decide("approve")} disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="approve-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Setujui</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {canDisburse && (
          <DisburseDialog req={req} onDone={load} trigger={
            <Button className="bg-brand hover:bg-emerald-800" data-testid="disburse-btn">
              <Wallet className="h-4 w-4 mr-2" /> Cairkan Kas
            </Button>
          } />
        )}
      </div>

      <Card className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Info icon={UserCircle} label="Pemohon" value={req.requester_name} />
          <Info icon={Building2} label="Divisi" value={req.divisi} />
          <Info icon={Wallet} label="Jumlah Diminta" value={formatRupiah(req.amount_requested)} mono />
          <Info icon={CheckCircle2} label="Jumlah Disetujui" value={req.approved_amount != null ? formatRupiah(req.approved_amount) : "-"} mono />
        </div>
        <div className="mt-5 pt-5 border-t">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Alasan Penggunaan</p>
          <p className="text-sm">{req.reason}</p>
        </div>
        {req.need_proof_file_ids?.length > 0 && (
          <div className="mt-5 pt-5 border-t">
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Bukti Kebutuhan</p>
            <div className="flex flex-wrap gap-2">
              {req.need_proof_file_ids.map((fid, i) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline border rounded-lg px-3 py-1.5">
                  <FileText className="h-4 w-4" /> Lampiran {i + 1}
                </a>
              ))}
            </div>
          </div>
        )}
        {req.decision_note && (
          <div className="mt-5 pt-5 border-t">
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" /> Catatan {req.approver_name ? `oleh ${req.approver_name}` : ""}
            </p>
            <p className="text-sm">{req.decision_note}</p>
          </div>
        )}
        {isAdmin && req.status !== "pending" && (
          <div className="mt-4 pt-4 border-t flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>Riwayat approval: {req.approver_name || "-"} · {formatDate(req.decided_at)}</span>
            <AdminDeleteButton path={`/admin/reset/approval/${req.id}`} onDone={load} label="Hapus Approval" testId="admin-reset-approval"
              confirmText="Approval dihapus permanen. Pengajuan kembali ke status Menunggu Approval; pencairan dan bukti pengeluaran (jika ada) ikut terhapus." />
          </div>
        )}
      </Card>

      {/* Accountability section */}
      {(req.status === "approved" || req.status === "disbursed" || req.status === "completed") && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-5 w-5 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Penggunaan Kas (Bukti Pengeluaran)</h3>
          </div>
          {req.status === "approved" && (
            <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mb-4">
              <Wallet className="h-4 w-4" /> Menunggu pencairan kas oleh Tim Keuangan. Bukti penggunaan kas baru bisa diinput setelah kas dicairkan.
            </div>
          )}
          {req.disbursed_by && (
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs text-muted-foreground font-mono">Dicairkan oleh {req.disbursed_by} · {formatDate(req.disbursed_at)}{req.disburse_source && <span className="block text-emerald-800" data-testid="disburse-source">Sumber dana: {sourceLabel(req.disburse_source)}</span>}</p>
              <AdminDeleteButton path={`/admin/reset/disbursement/${req.id}`} onDone={load} label="Hapus Pencairan" testId="admin-reset-disbursement"
                confirmText="Pencairan dihapus permanen. Pengajuan kembali ke status Disetujui dan semua bukti pengeluarannya ikut terhapus." />
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-lg bg-secondary p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Diterima</p>
              <p className="font-mono font-semibold text-sm">{formatRupiah(approved)}</p>
            </div>
            <div className="rounded-lg bg-secondary p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Total Bukti</p>
              <p className="font-mono font-semibold text-sm text-emerald-700">{formatRupiah(req.total_expensed)}</p>
            </div>
            <div className="rounded-lg bg-secondary p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Sisa Saldo</p>
              <p className="font-mono font-semibold text-sm text-rose-600" data-testid="remaining-balance">{formatRupiah(req.remaining_balance)}</p>
            </div>
          </div>
          <Progress value={pct} className="h-2 mb-4" data-testid="accountability-progress" />

          {req.expense_proofs?.length > 0 && (
            <ul className="space-y-2 mb-4">
              {req.expense_proofs.map((p, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border" data-testid={`expense-proof-${i}`}>
                  <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-brand hover:underline min-w-0">
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">{p.description || `Bukti ${i + 1}`}</span>
                  </a>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-sm font-semibold">{formatRupiah(p.amount)}</span>
                    {isAdmin && <EditExpenseDialog item={p} url={`/admin/cash-requests/${req.id}/proofs/${i}`} vehicles={vehicles} onDone={load} testId={`edit-proof-btn-${i}`} />}
                    <AdminDeleteButton path={`/admin/sub/proofs/${req.id}/${i}`} onDone={load} iconOnly testId={`admin-del-proof-${i}`} confirmText="Bukti pengeluaran dihapus permanen. Sisa saldo kas kembali bertambah." />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {req.status === "completed" && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <CheckCircle2 className="h-4 w-4" /> Pertanggungjawaban lengkap. Pengajuan selesai.
            </div>
          )}

          {canUploadProof && (
            <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-3" data-testid="goto-expenses">
              <p className="text-sm text-muted-foreground">Input bukti penggunaan kas kini ada di menu <strong>Pengeluaran</strong>.</p>
              <Button onClick={() => navigate("/expenses")} className="bg-brand hover:bg-emerald-800" data-testid="goto-expenses-btn">
                <Receipt className="h-4 w-4 mr-2" /> Buat Pengeluaran
              </Button>
            </div>
          )}
        </Card>
      )}
      <EditHistory entityType="cash_request" entityId={req.id} refreshKey={rk} />
    </div>
  );
}
